import React, { useState, useCallback, useMemo } from 'react';
import { Button, IconButton, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as ExcelJS from 'exceljs';
import fileDownload from 'js-file-download';
import { getAttendanceListByEventId } from 'src/services/AttendanceService';
 
interface DownloadAttendanceProps {
  eventId: number;
 
  eventName: string;
  eventDate: string;
  eventTime: string;
  onMessage: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}
 
 
const COLORS = {
  PRIMARY: 'FF0e1f5f',
  SECONDARY: 'FF2d6ebe',
  LIGHT_BLUE: 'FFF0F8FF',
  LIGHT_GRAY: 'FFF8F9FA',
  WHITE: 'FFFFFFFF',
  DARK_GRAY: 'FF333333',
  PURPLE_LIGHT: 'FFE6E9FF'
} as const;
 
const STYLES = {
  HEADER: {
    font: { bold: true, size: 16, color: { argb: COLORS.WHITE } },
    alignment: { horizontal: 'center' as const, vertical: 'middle' as const },
    fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.PRIMARY } }
  },
  TABLE_HEADER: {
    font: { bold: true, size: 12, color: { argb: COLORS.WHITE } },
    fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.SECONDARY } },
    alignment: { horizontal: 'center' as const, vertical: 'middle' as const }
  },
  EVENT_DETAIL_LABEL: {
    font: { bold: true, size: 12 },
    fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: COLORS.LIGHT_BLUE } }
  },
  EVENT_DETAIL_VALUE: {
    font: { size: 12, color: { argb: COLORS.DARK_GRAY } }
  },
  CELL_BORDER: {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const }
  }
} as const;
 
const DownloadAttendance: React.FC<DownloadAttendanceProps> = ({
  eventId,
  eventName,
  eventDate,
  eventTime,
  onMessage
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
 
const isEventInFuture = eventDate && new Date(eventDate) > new Date();
 
 
const fileName = useMemo(() => {
  const sanitizedEventName = eventName
    ? eventName.replace(/[^a-zA-Z0-9\s\-_.]/g, '').trim()
    : 'Event';
  return `${sanitizedEventName}_Attendance_Report.xlsx`;
}, [eventName]);
 
 
  const applyCellStyle = useCallback((cell: ExcelJS.Cell, style: any) => {
    Object.assign(cell, style);
  }, []);
 
 
  const setCellBorders = useCallback((worksheet: ExcelJS.Worksheet, cells: string[]) => {
    cells.forEach(cellRef => {
      worksheet.getCell(cellRef).border = STYLES.CELL_BORDER;
    });
  }, []);
 
  const createEventHeader = useCallback((worksheet: ExcelJS.Worksheet) => {
    worksheet.mergeCells('A1:C1');
    const headerCell = worksheet.getCell('A1');
    headerCell.value = 'Event Attendance Report';
    applyCellStyle(headerCell, STYLES.HEADER);
    worksheet.getRow(1).height = 30;
  }, [applyCellStyle]);
 
  const createEventDetails = useCallback((worksheet: ExcelJS.Worksheet) => {
    const details = [
      ['Event Name:', eventName || 'N/A'],
      ['Event Date:', eventDate || 'N/A'],
      ['Event Time:', eventTime || 'N/A']
    ];
 
    details.forEach(([label, value], index) => {
      const row = 3 + index;
      const labelCell = worksheet.getCell(`A${row}`);
      const valueCell = worksheet.getCell(`B${row}`);
     
      labelCell.value = label;
      valueCell.value = value;
     
      applyCellStyle(labelCell, STYLES.EVENT_DETAIL_LABEL);
      applyCellStyle(valueCell, STYLES.EVENT_DETAIL_VALUE);
    });
 
    worksheet.getRow(6).height = 10; // Spacing
  }, [eventName, eventDate, eventTime, applyCellStyle]);
 
  const createTableHeaders = useCallback((worksheet: ExcelJS.Worksheet) => {
    const headers = ['No', 'Attendee Name', 'Participation Status'];
    const cells = ['A7', 'B7', 'C7'];
   
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(cells[index]);
      cell.value = header;
      applyCellStyle(cell, STYLES.TABLE_HEADER);
      cell.border = STYLES.CELL_BORDER;
    });
  }, [applyCellStyle]);
 
 
  const populateAttendanceData = useCallback((worksheet: ExcelJS.Worksheet, attendees: any[]) => {
    attendees.forEach((attendee, index) => {
      const rowNum = 8 + index;
      const cells = [`A${rowNum}`, `B${rowNum}`, `C${rowNum}`];
 
      worksheet.getCell(`A${rowNum}`).value = index + 1;
      worksheet.getCell(`B${rowNum}`).value = attendee.employeeName;
      worksheet.getCell(`C${rowNum}`).value = isEventInFuture ? '' : (attendee.participations || 'N/A');
 
      cells.forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.border = STYLES.CELL_BORDER;
        cell.alignment = { vertical: 'middle' };
        cell.font = { size: 11 };
       
        // Center align S.No and Status columns
        if (cellRef.startsWith('A') || cellRef.startsWith('C')) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
      });
 
      // Alternate row background
      if (index % 2 === 1) {
        cells.forEach(cellRef => {
          worksheet.getCell(cellRef).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: COLORS.LIGHT_GRAY }
          };
        });
      }
    });
  }, [isEventInFuture]);
 
 
const autoFitColumns = useCallback((worksheet: ExcelJS.Worksheet) => {
  worksheet.columns.forEach((column) => {
    let maxLength = 10;
    worksheet.eachRow((row) => {
      const cell = row.getCell(column.number);
      let valueStr = '';
      const cellValue = cell.value;
      if (cellValue == null) {
        valueStr = '';
      } else if (
        typeof cellValue === 'object' &&
        cellValue !== null &&
        'richText' in cellValue &&
        Array.isArray((cellValue as ExcelJS.CellRichTextValue).richText)
      ) {
        valueStr = (cellValue as ExcelJS.CellRichTextValue).richText.map((rt: any) => rt.text).join('');
      } else {
        valueStr = cellValue.toString();
      }
      if (valueStr.length > maxLength) {
        maxLength = valueStr.length;
      }
    });
    column.width = Math.min(maxLength + 3, 30); // Reduced buffer and max width
  });
}, []);
 
  // Function to create summary section
  const createSummarySection = useCallback((worksheet: ExcelJS.Worksheet, attendees: any[]) => {
    const summaryStartRow = 8 + attendees.length + 2;
   
    if (!isEventInFuture) {
      const stats: { total: number; participated: number; absent: number; notRecorded: number } = {
        total: attendees.length,
        participated: attendees.filter(a => ['Present', 'Facilitator'].includes(a.participations)).length,
        absent: attendees.filter(a => a.participations === 'Absent').length,
        notRecorded: 0 // initialize
      };
      stats.notRecorded = stats.total - stats.participated - stats.absent;
 
      // Summary header
      worksheet.mergeCells(`A${summaryStartRow}:C${summaryStartRow}`);
      const summaryHeader = worksheet.getCell(`A${summaryStartRow}`);
      summaryHeader.value = 'Attendance Summary';
      summaryHeader.font = { bold: true, size: 14, color: { argb: COLORS.WHITE } };
      summaryHeader.alignment = { horizontal: 'center', vertical: 'middle' };
      summaryHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.PRIMARY } };
      worksheet.getRow(summaryStartRow).height = 25;
 
      // Summary data
      const summaryData = [
        ['Total Attendees:', stats.total],
        ['Participated (Present + Facilitator):', stats.participated],
        ['Not Participated (Absent):', stats.absent],
        ...(stats.notRecorded > 0 ? [['Not Recorded:', stats.notRecorded]] : [])
      ];
 
      summaryData.forEach(([label, value], index) => {
        const row = summaryStartRow + 2 + index;
        worksheet.getCell(`A${row}`).value = label;
        worksheet.getCell(`B${row}`).value = value;
        worksheet.getCell(`A${row}`).font = { bold: true, size: 11 };
        worksheet.getCell(`B${row}`).font = { size: 11, color: { argb: COLORS.DARK_GRAY } };
        worksheet.getCell(`A${row}`).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: COLORS.LIGHT_BLUE }
        };
      });
    } else {
      worksheet.mergeCells(`A${summaryStartRow}:C${summaryStartRow}`);
      const totalCell = worksheet.getCell(`A${summaryStartRow}`);
      totalCell.value = `Total Registered Attendees: ${attendees.length}`;
      totalCell.font = { bold: true, size: 12 };
      totalCell.alignment = { horizontal: 'center' };
      totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.PURPLE_LIGHT } };
    }
  }, [isEventInFuture]);
 
  const handleDownloadAttendance = useCallback(async () => {
    try {
      setIsDownloading(true);
     
      const res = await getAttendanceListByEventId(eventId);
      const attendees = res?.data?.data || [];
 
      if (!attendees?.length) {
        onMessage('No data available to download.', 'warning');
        return;
      }
 
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Attendance Report');
 
      // Create report sections
      createEventHeader(worksheet);
      createEventDetails(worksheet);
      createTableHeaders(worksheet);
      populateAttendanceData(worksheet, attendees);
      createSummarySection(worksheet, attendees);
      autoFitColumns(worksheet);
 
      // Generate and download file
      const buffer = await workbook.xlsx.writeBuffer();
      fileDownload(buffer, fileName);
     
      onMessage('Attendee Report Downloaded Successfully', 'success');
    } catch (error) {
      console.error('Download error:', error);
      onMessage('Error Downloading Report', 'error');
    } finally {
      setIsDownloading(false);
    }
  }, [
    eventId,
    fileName,
    onMessage,
    createEventHeader,
    createEventDetails,
    createTableHeaders,
    populateAttendanceData,
    autoFitColumns,
    createSummarySection
  ]);
 
  return (
    <Tooltip title="Download Attendance List" placement="top">
      <IconButton
        onClick={handleDownloadAttendance}
        disabled={isDownloading}
        size="small"
      >
        <DownloadIcon
          style={{
            fontSize: '20px',
            cursor: 'pointer',
          }}
        />
      </IconButton>
    </Tooltip>
  );
};
 
export default DownloadAttendance;
 